// eyeq — QA reviewer tool contracts (eyeq.weern.com #/qa, auth class 'qa':
// role 'qa' or 'manager' by set membership, never by rank — ranking qa above
// lead would hand reviewers the lead-class team surface).
//
// The queue is the bulk intake (source_pipeline 'eyeq-intake', status 'qa' —
// the one enum slot nothing else reads or writes, so 56k imported frames stay
// invisible to the manager overview, the url-backfill counter and the link
// channel). Stage 1 only: accept / PII / reject / move-to-correct-cell. A
// stage-1 accept keeps status 'qa' with qa_verdict 'accepted'; promotion to
// 'accepted' belongs to stage 2 (link verification), which this tool stubs.
import { z } from 'zod';
import { EyeqMemberRoleSchema } from './cabinet';

// ── GET /api/eyeq/qa/summary ────────────────────────────────────────────────
// One aggregation scan: `cells` carries every non-empty (locale, category,
// sub_category) queue bucket; the client folds it into the home grid, the
// subcategory list and the review-screen country chips. `tree` is the full
// taxonomy (including empty cells) for the reassign sheet, in seed order.
export const EyeqQaCellSchema = z.object({
  locale: z.string(),
  category: z.string(),
  sub_category: z.string(),
  queued: z.number().int(),   // unreviewed intake frames VISIBLE to a reviewer (in the cell's remaining capacity; ADR-0088)
  queued_hidden: z.number().int().default(0), // unreviewed frames beyond the cell's remaining capacity — not shown
  decided: z.number().int(),  // intake frames here that already carry a verdict
  // Какого трека ячейка: кадры без ссылки и кадры со ссылкой — это два РАЗНЫХ
  // типа материала, а не две стадии одного (решение владельца 2026-08-17).
  url_required: z.boolean().default(false),
});
export type EyeqQaCell = z.infer<typeof EyeqQaCellSchema>;

export const EyeqQaTreeSubSchema = z.object({
  sub_category: z.string(),
  url_required: z.boolean(),
  plan: z.number().int(), // sum over locales of the quota effective target
});
export const EyeqQaTreeCatSchema = z.object({
  category: z.string(),
  subs: z.array(EyeqQaTreeSubSchema),
});
export type EyeqQaTreeCat = z.infer<typeof EyeqQaTreeCatSchema>;

export const EyeqQaSummaryResSchema = z.object({
  pending: z.number().int(),     // total unreviewed intake frames
  links: z.number().int(),       // stage-2 queue size; 0 until stage 2 ships
  ready: z.number().int(),       // intake frames with qa_verdict 'accepted'
  shipped: z.number().int(),     // status 'shipped' across the catalog
  plan_total: z.number().int(),  // sum of quota effective targets
  cells: z.array(EyeqQaCellSchema),
  tree: z.array(EyeqQaTreeCatSchema),
});
export type EyeqQaSummaryRes = z.infer<typeof EyeqQaSummaryResSchema>;

// ── GET /api/eyeq/qa/queue?category=&sub_category=&locale=&limit=&after= ────
// Keyset paging by asset_id: the unreviewed set shrinks while two reviewers
// work, so offset paging silently skips frames. `after` is exclusive; ''
// starts from the lowest id. Decided frames stay in the page (their badges
// must survive a reload), so the filter is scope, not verdict.
// track: 'nolink' — кадры, которым ссылка не нужна; 'link' — которым нужна и
// которые приходят уже со ссылкой; '' — весь пул (поведение до разделения).
export const EyeqQaTrackSchema = z.enum(['', 'nolink', 'link']);
export type EyeqQaTrack = z.infer<typeof EyeqQaTrackSchema>;

export const EyeqQaQueueQuerySchema = z.object({
  track: EyeqQaTrackSchema.default(''),
  category: z.string().optional(),
  sub_category: z.string().optional(),
  locale: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  after: z.string().default(''),
  // 1 = only frames without a verdict (the working queue); 0 = everything in
  // scope, used when re-opening a session to restore badges.
  unreviewed: z.coerce.number().int().min(0).max(1).default(1),
});
export type EyeqQaQueueQuery = z.infer<typeof EyeqQaQueueQuerySchema>;

export const EyeqQaQueueItemSchema = z.object({
  asset_id: z.string(),
  locale: z.string(),
  filename: z.string(),
  category: z.string(),
  sub_category: z.string(),
  // What the annotation pipeline decided at import; differs from the current
  // cell after a 'move' — the review panel's «Было определено» row.
  ai_category: z.string(),
  ai_sub_category: z.string(),
  qa_verdict: z.enum(['', 'accepted', 'rejected']),
  reject_reason: z.string(),
  // Пиксели кадра — исключительно ради раскладки сетки: страница собирается
  // из кадров ОДНОЙ ориентации, иначе портреты и альбомы в одной решётке
  // оставляют рваные поля. 0 = размер неизвестен (у intake заполнено всё).
  width: z.number().int().default(0),
  height: z.number().int().default(0),
});
export type EyeqQaQueueItem = z.infer<typeof EyeqQaQueueItemSchema>;

export const EyeqQaQueueResSchema = z.object({
  items: z.array(EyeqQaQueueItemSchema),
  total: z.number().int(), // rows matching the scope filter (before paging)
  /** Непрозрачный курсор следующей страницы; null — очередь исчерпана. */
  next: z.string().nullable().default(null),
});
export type EyeqQaQueueRes = z.infer<typeof EyeqQaQueueResSchema>;

// GET /api/eyeq/qa/frame/:asset_id/image?size=thumb|prev|roi|full — binary
// endpoint: R2 sibling <r2_key>.thumb.webp → <r2_key>.prev.webp → original
// (legacy .jpg siblings still resolve), best available at or above the asked
// tier. Query contract only, no res schema per contracts.md.
//
// 'roi' is a 1:1 crop in NATIVE pixels around the sharpest tile, generated
// only for frames the offline blur metric flags as suspect: a downscaled
// preview cannot settle sharpness (a 1600px preview of a 5000px original
// carries a third of the detail), and pulling the multi-megabyte original
// for every doubt is what the previews exist to avoid. Absent 'roi' answers
// 404 rather than falling back to the original.
export const EyeqQaImageSizeSchema = z.enum(['thumb', 'prev', 'roi', 'full']);

// ── POST /api/eyeq/qa/decide ────────────────────────────────────────────────
// One click = one decision (the design's four buttons):
//   accept → qa_verdict 'accepted', status stays 'qa'
//   pii    → qa_verdict 'rejected', reject_reason 'pii', status 'rejected'
//   reject → qa_verdict 'rejected', reject_reason 'other', status 'rejected'
//   move   → meta cell + cat_id updated, then treated as accept; the import
//            values stay in ai_category/ai_sub_category
// The UPDATE is conditional on qa_verdict = '' — with two reviewers on one
// queue, the second decision on a frame gets 409 {error:'already_decided'}
// instead of silently overwriting the first.
export const EyeqQaDecisionSchema = z.enum(['accept', 'pii', 'reject', 'move']);
export type EyeqQaDecision = z.infer<typeof EyeqQaDecisionSchema>;

export const EyeqQaDecideReqSchema = z.object({
  asset_id: z.string().min(1),
  decision: EyeqQaDecisionSchema,
  // move only — both required together (enforced in the handler so the
  // refusal names the field); MEQ 'and' spelling.
  category: z.string().trim().min(1).max(80).optional(),
  sub_category: z.string().trim().min(1).max(120).optional(),
});
export type EyeqQaDecideReq = z.infer<typeof EyeqQaDecideReqSchema>;

export const EyeqQaDecideResSchema = z.object({
  ok: z.boolean(),
  asset_id: z.string(),
  qa_verdict: z.enum(['accepted', 'rejected']),
  reject_reason: z.string(),
  category: z.string(),
  sub_category: z.string(),
  qa_at: z.string(),
});
export type EyeqQaDecideRes = z.infer<typeof EyeqQaDecideResSchema>;

// ── POST /api/eyeq/qa/decideBulk ────────────────────────────────────────────
// Сетка: ревьюер отмечает пачку кадров чекбоксами и решает их одним действием
// (запрос владельца 2026-08-31 — «во многих кадрах сразу видно, что это то или
// не то»). Семантика решения ровно та же, что у одиночного decide; отличие в
// том, что частичный успех — норма: кадр, который успел решить другой ревьюер,
// попадает в `skipped`, а остальные записываются. Потолок 200 держит один
// запрос в пределах лимитов D1 и не даёт промаху выделения снести полбазы.
export const EyeqQaBulkDecideReqSchema = z.object({
  asset_ids: z.array(z.string().min(1)).min(1).max(200),
  decision: EyeqQaDecisionSchema,
  category: z.string().trim().min(1).max(80).optional(),
  sub_category: z.string().trim().min(1).max(120).optional(),
});
export type EyeqQaBulkDecideReq = z.infer<typeof EyeqQaBulkDecideReqSchema>;

export const EyeqQaBulkDecideResSchema = z.object({
  ok: z.boolean(),
  qa_verdict: z.enum(['accepted', 'rejected']),
  reject_reason: z.string(),
  qa_at: z.string(),
  // Ячейка после решения: у 'move' — новая, иначе пустые строки (у каждого
  // кадра в пачке своя, и подменять её одной на всех нельзя).
  category: z.string(),
  sub_category: z.string(),
  decided: z.array(z.string()),
  skipped: z.array(z.object({ asset_id: z.string(), error: z.string() })),
});
export type EyeqQaBulkDecideRes = z.infer<typeof EyeqQaBulkDecideResSchema>;

// ── POST /api/eyeq/qa/undo ──────────────────────────────────────────────────
// Снятие СВОЕГО вердикта: кадр возвращается в очередь без вердикта. Отмена,
// живущая только в браузере, испарялась бы при перезагрузке, поэтому это
// настоящее изменение состояния (тот же приём, что `verdict:'clear'` в
// менеджерском туле). Два гейта: снять может только тот, кто поставил
// (менеджер — любой), и только пока кадр не уехал в поставку — у отгруженного
// кадра вердикт часть инварианта поставки. Ячейку 'move' отмена НЕ откатывает:
// кадр возвращается в очередь там, куда его перенесли, и решается заново.
export const EyeqQaUndoReqSchema = z.object({
  asset_id: z.string().min(1),
});
export type EyeqQaUndoReq = z.infer<typeof EyeqQaUndoReqSchema>;

export const EyeqQaUndoResSchema = z.object({
  ok: z.boolean(),
  asset_id: z.string(),
  qa_verdict: z.literal(''),
  category: z.string(),
  sub_category: z.string(),
});
export type EyeqQaUndoRes = z.infer<typeof EyeqQaUndoResSchema>;

// ── GET /api/eyeq/qa/week ───────────────────────────────────────────────────
// The reviewer's own 7-day tape, computed from eyeq_audit (qa_* actions by
// actor) — NOT from qa_at, which is overwritten on re-decide and cleared by
// undo, so streaks built on it would lie.
export const EyeqQaWeekDaySchema = z.object({
  date: z.string(), // YYYY-MM-DD, UTC
  count: z.number().int(),
});
export const EyeqQaWeekResSchema = z.object({
  days: z.array(EyeqQaWeekDaySchema).length(7), // oldest first, ends today
  streak: z.number().int(),                     // consecutive non-zero days ending today or yesterday
  best: EyeqQaWeekDaySchema.nullable(),         // null until any decision exists
});
export type EyeqQaWeekRes = z.infer<typeof EyeqQaWeekResSchema>;

// ── POST /api/eyeq/upload/intakeImport (machine) ────────────────────────────
// Bulk registration of already-uploaded intake frames. The worker owns every
// derived field a Python fork would otherwise duplicate: asset_id/uuid
// minting, cat_id, country from the locale, place_details assembly and
// day-phase outdoor lighting. Idempotent: an asset whose r2_key is already
// indexed is reported in `skipped`, not re-inserted.
export const EyeqIntakeImportRowSchema = z.object({
  r2_key: z.string().min(1).max(500),
  locale: z.string().min(1),
  category: z.string().min(1),     // MEQ 'and' spelling
  sub_category: z.string().min(1),
  subject_name: z.string().max(300),
  disambiguation_details: z.string().max(3000),
  url: z.string().max(2048).default(''),
  has_text: z.boolean(),
  indoor_outdoor: z.string(),
  indoor_lighting: z.string().default(''),
  place_type: z.string().default(''),
  camera_make: z.string().max(80),
  camera_model: z.string().max(80),
  captured_at: z.string().max(40).default(''), // EXIF 'YYYY:MM:DD HH:MM:SS'
  gps_present: z.union([z.literal(0), z.literal(1)]),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  bytes: z.number().int().positive(),
  dhash: z.string().default(''),
});
export type EyeqIntakeImportRow = z.infer<typeof EyeqIntakeImportRowSchema>;

export const EyeqIntakeImportReqSchema = z.object({
  rows: z.array(EyeqIntakeImportRowSchema).min(1).max(50),
});
export type EyeqIntakeImportReq = z.infer<typeof EyeqIntakeImportReqSchema>;

// ── POST /api/eyeq/upload/intakeLinks (ingest) ──────────────────────────────
// Bulk URL backfill for frames already in the catalog: the offline link
// collector answers the reference-page question for tens of thousands of
// intake frames at once, which no per-task route can do at that scale.
//
// The ≤10-per-product-per-country cap is deliberately NOT applied here. The
// index is the complete record of what each asset IS, and a reference page is
// part of that record; the cap is a DELIVERY rule and belongs where a
// shipment is assembled (`shipIssueFor` → 'url_over_cap'). Applying it at
// write time would leave the index permanently missing links we already know
// — and a wiki article about a museum is one page for hundreds of distinct
// exhibits, so the cap would refuse the very links this route exists to store.
// The FORMAT gate still runs: a malformed URL is refused, never stored.
export const EyeqIntakeLinkRowSchema = z.object({
  asset_id: z.string().min(1).max(64),
  // 'N/A' is the protocol's answer for objects that genuinely have no page.
  url: z.string().trim().max(2048),
});
export type EyeqIntakeLinkRow = z.infer<typeof EyeqIntakeLinkRowSchema>;

export const EyeqIntakeLinksReqSchema = z.object({
  rows: z.array(EyeqIntakeLinkRowSchema).min(1).max(50),
  // Default false: a link already in the index was put there by a human
  // decision (QA accept) and is not overwritten by a bulk run.
  overwrite: z.boolean().default(false),
});
export type EyeqIntakeLinksReq = z.infer<typeof EyeqIntakeLinksReqSchema>;

export const EyeqIntakeLinksResSchema = z.object({
  ok: z.boolean(),
  updated: z.number().int(),
  skipped: z.number().int(),   // unknown asset, not an intake row, or url already set
  errors: z.array(z.object({ asset_id: z.string(), note: z.string() })),
});
export type EyeqIntakeLinksRes = z.infer<typeof EyeqIntakeLinksResSchema>;

// ── POST /api/eyeq/qa/previewReady (machine) ────────────────────────────────
// The offline preview generator reports frames whose R2 siblings are uploaded.
// Until a frame is reported, the queue and the counters do not show it at all:
// during a multi-hour generation run the reviewer works on what is ready
// instead of waiting, and never opens a frame that would drag the
// multi-megabyte original. Idempotent — re-reporting a frame is a no-op.
export const EyeqPreviewReadyRowSchema = z.object({
  asset_id: z.string().min(1),
  // Grid-max Laplacian on native tiles; a frame below the ROI threshold also
  // carries a 1:1 crop sibling. Null when the generator could not measure.
  sharpness: z.number().nullable().default(null),
  roi: z.boolean().default(false),
});
export const EyeqPreviewReadyReqSchema = z.object({
  rows: z.array(EyeqPreviewReadyRowSchema).min(1).max(200),
});
export type EyeqPreviewReadyReq = z.infer<typeof EyeqPreviewReadyReqSchema>;

export const EyeqPreviewReadyResSchema = z.object({
  ok: z.boolean(),
  updated: z.number().int(),
});
export type EyeqPreviewReadyRes = z.infer<typeof EyeqPreviewReadyResSchema>;

export const EyeqIntakeImportResSchema = z.object({
  ok: z.boolean(),
  inserted: z.number().int(),
  skipped: z.number().int(), // r2_key already indexed
  errors: z.array(z.object({ r2_key: z.string(), note: z.string() })),
});
export type EyeqIntakeImportRes = z.infer<typeof EyeqIntakeImportResSchema>;

// ── Приглашение СОТРУДНИКА по ссылке ────────────────────────────────────────
// Не путать с EyeqInvite* из eyeq/network.ts — те про сеть участников
// (issuer_uid, страна, локали, мультипликаторы). Здесь речь о служебной роли.
// POST /api/eyeq/cabinet/claimInvite — класс PUBLIC по маршруту, но требует
// валидный Firebase Bearer: у приглашённого ещё НЕТ строки в eyeq_members, а
// участник-класс отказал бы ему до входа в обработчик. Токен приходит из
// ссылки вида https://eyeq.weern.com/?invite=<token>.
//
// Ссылка выдаёт доступ к чужим данным, поэтому она расходуемая: ограничена по
// числу людей и по сроку, отзывается мгновенно. Повторный вход того же uid
// использование НЕ тратит — иначе второй телефон получателя упёрся бы в отказ.
export const EyeqStaffInviteClaimReqSchema = z.object({
  token: z.string().trim().min(16).max(128),
});
export type EyeqStaffInviteClaimReq = z.infer<typeof EyeqStaffInviteClaimReqSchema>;

export const EyeqStaffInviteClaimResSchema = z.object({
  ok: z.boolean(),
  role: z.string(),
  // already = роль уже была у этого uid (повторный переход по ссылке)
  already: z.boolean(),
});
export type EyeqStaffInviteClaimRes = z.infer<typeof EyeqStaffInviteClaimResSchema>;

// POST /api/eyeq/_admin/invite (machine) — выписать ссылку.
export const EyeqStaffInviteCreateReqSchema = z.object({
  // Роль берём из общего объявления, а не перечисляем заново (rules/contracts).
  role: EyeqMemberRoleSchema,
  max_uses: z.number().int().min(1).max(50).default(1),
  days: z.number().int().min(1).max(90).default(14),
  note: z.string().trim().max(200).default(''),
});
export type EyeqStaffInviteCreateReq = z.infer<typeof EyeqStaffInviteCreateReqSchema>;

export const EyeqStaffInviteCreateResSchema = z.object({
  ok: z.boolean(),
  token: z.string(),
  url: z.string(),
  role: z.string(),
  max_uses: z.number().int(),
  expires_at: z.string(),
});
export type EyeqStaffInviteCreateRes = z.infer<typeof EyeqStaffInviteCreateResSchema>;

// ── POST /api/eyeq/upload/poolFlags (ingest) ────────────────────────────────
// Признаки отбора из офлайн-конвейера (ADR-0055). Воркер их не выводит: людей
// в кадре судит зрительная модель, а «влезает ли кадр в квоту» — сквозная
// нумерация внутри ячейки по всему каталогу. Перезаливается целиком при
// каждом прогоне конвейера, поэтому запись идемпотентна по asset_id.
export const EyeqPoolFlagRowSchema = z.object({
  asset_id: z.string().min(1).max(64),
  strict_ok: z.boolean(),
  in_target: z.boolean(),
  link_required: z.boolean(),
  // Жёсткий запрет: люди в кадре (distant или prominent), решение владельца
  // строже протокола, который запрещает только prominent.
  people_bad: z.boolean(),
  age_days: z.number().int().nullable().default(null),
});
export type EyeqPoolFlagRow = z.infer<typeof EyeqPoolFlagRowSchema>;

export const EyeqPoolFlagsReqSchema = z.object({
  rows: z.array(EyeqPoolFlagRowSchema).min(1).max(50),
});
export type EyeqPoolFlagsReq = z.infer<typeof EyeqPoolFlagsReqSchema>;

export const EyeqPoolFlagsResSchema = z.object({
  ok: z.boolean(),
  written: z.number().int(),
  skipped: z.number().int(),   // asset_id не найден в каталоге
});
export type EyeqPoolFlagsRes = z.infer<typeof EyeqPoolFlagsResSchema>;
